import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';

import * as faceapi from 'face-api.js';
import { FaceMesh } from '@mediapipe/face_mesh';
import { Camera } from '@mediapipe/camera_utils';
import { useRequest } from 'src/hooks';
import { useNotif } from 'src/hooks';
import { dataURLToFile } from 'src/helpers';

import { useFRcamera } from '../../useAiCamera';
import { not } from 'mathjs';

const FaceBioContext = createContext({
  webcamRef: null,
  canvasRef: null,
  loadingModels: false,
  faceDetected: {
    face: false,
    details: { confidence: 0, name: '' }
  },
  allowCapture: false,
  capturedImage: null,
  capture: () => {},
  bioID: '',
  handleSetBioID: () => {},
  openBioSelfie: false,
  setOpenBioSelfie: () => {},
  handleOpen: () => {},
  handleClose: () => {}
});

const compressImageToTargetSize = async (input, maxSize = 0, quality = 0) => {
  const loadImage = src =>
    new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => resolve(img);
      img.onerror = reject;

      img.src = src;
    });

  const getDataUrl = (img, size, quality) => {
    const originalWidth = img.naturalWidth || img.width;
    const originalHeight = img.naturalHeight || img.height;

    const scale = Math.min(1, size / Math.max(originalWidth, originalHeight));

    const width = Math.round(originalWidth * scale);
    const height = Math.round(originalHeight * scale);

    const canvas = document.createElement('canvas');

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(img, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', quality);
  };

  let img;

  if (input instanceof File || input instanceof Blob) {
    const reader = new FileReader();

    const base64 = await new Promise(resolve => {
      reader.onload = e => resolve(e.target.result);
      reader.readAsDataURL(input);
    });

    img = await loadImage(base64);
  } else {
    img = await loadImage(input);
  }

  return getDataUrl(img, maxSize, quality);
};
function FaceBioProvider({ children }) {
  const notify = useNotif();
  const { dtImg, processFace, settings } = useFRcamera();

  const { landmark = false, value = [] } = settings || {};
  const [tableData, setTableData] = useState([]);

  const request = useRequest();

  const [openBioSelfie, setOpenBioSelfie] = useState(false);
  const [selected, setSelected] = useState([]);
  const [empData, setEmpData] = useState([]);
  const [imgSrc, setImgSrc] = useState([]);

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const faceMatcherRef = useRef(null);
  const modelsReadyRef = useRef(false);
  const processingBioRef = useRef(false);
  const submittedRef = useRef(false);
  const selectedRef = useRef([]);

  const [loadingModels, setLoadingModels] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  const now = new Date();

  const [faceDetected, setFaceDetected] = useState({
    face: false,
    details: {
      confidence: 0,
      name: ''
    }
  });

  const [allowCapture, setAllowCapture] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [bioID, setBioID] = useState('');
  const [resEmpData, setResEmpData] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingTable, setLoadingTable] = useState(false);

  const loadModels = async () => {
    try {
      setLoadingModels(true);

      const MODEL_URL = `${window.location.origin}/models`;

      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
      ]);

      modelsReadyRef.current = true;
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingModels(false);
    }
  };

  const loadLabeledImages = async ({ selectedLabel, images = [] }) => {
    if (!selectedLabel || !images?.length) {
      return [];
    }

    const loadImage = src =>
      new Promise((resolve, reject) => {
        const img = new Image();

        img.crossOrigin = 'anonymous';

        img.onload = () => resolve(img);
        img.onerror = reject;

        img.src = src;
      });

    const descriptions = [];

    for (let i = 0; i < images.length; i++) {
      try {
        const img = await loadImage(images[i]);

        const detection = await faceapi
          .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (detection) {
          descriptions.push(detection.descriptor);
        }
      } catch (err) {
        console.error(err);
      }
    }

    if (!descriptions.length) {
      return [];
    }

    return [
      new faceapi.LabeledFaceDescriptors(String(selectedLabel), descriptions)
    ];
  };

  const handleSetBioID = async ({ id, images = [] }) => {
    if (!id) return false;

    id = String(id);

    setBioID(id);

    faceMatcherRef.current = null;

    setAllowCapture(false);

    setFaceDetected({
      face: false,
      details: {
        confidence: 0,
        name: ''
      }
    });

    if (!modelsReadyRef.current) return false;

    processingBioRef.current = true;

    try {
      const labeledDescriptors = await loadLabeledImages({
        selectedLabel: id,
        images
      });

      if (!labeledDescriptors.length) {
        faceMatcherRef.current = null;

        return false;
      }

      faceMatcherRef.current = new faceapi.FaceMatcher(labeledDescriptors, 0.6);

      return true;
    } catch (err) {
      console.error(err);

      faceMatcherRef.current = null;

      return false;
    } finally {
      processingBioRef.current = false;
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const drawResult = (video, detection, label) => {
    const canvas = canvasRef.current;

    if (!canvas || !detection) return;

    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const box = detection.detection.box;

    ctx.save();

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);

    ctx.strokeStyle = label === 'Unknown' ? '#ff4444' : '#00ff00';

    ctx.lineWidth = 2;

    ctx.strokeRect(box.x, box.y, box.width, box.height);

    ctx.fillStyle = ctx.strokeStyle;
    ctx.font = '18px Arial';
    // ctx.fillText(label, box.x, box.y - 10);

    ctx.restore();
  };

  const drawLandmarks = results => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    canvas.width = results.image.width;
    canvas.height = results.image.height;

    canvas.style.width = '100%';
    canvas.style.height = '102%';

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!results.multiFaceLandmarks) return;

    ctx.save();

    ctx.scale(-1, 1);
    ctx.translate(-canvas.width, 0);

    ctx.fillStyle = '#ffffff';

    for (const landmarks of results.multiFaceLandmarks) {
      landmarks.forEach(p => {
        ctx.beginPath();

        ctx.arc(p.x * canvas.width, p.y * canvas.height, 1, 0, 2 * Math.PI);

        ctx.fill();
      });
    }

    ctx.restore();
  };

  const recognizeFace = async video => {
    if (processingBioRef.current) return null;

    const matcher = faceMatcherRef.current;

    if (!matcher) return null;

    const detection = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (!detection) {
      setAllowCapture(false);

      return null;
    }

    if (!matcher) return null;

    const bestMatch = matcher.findBestMatch(detection.descriptor);

    const confidence = Math.max(0, (1 - bestMatch.distance) * 100);

    let label = bestMatch.label;

    if (confidence < 55 || label === 'unknown') {
      label = 'Unknown';
    }

    setAllowCapture(label !== 'Unknown');

    return {
      detection,
      label
    };
  };

  const initFaceMesh = () => {
    const video = webcamRef.current?.video;

    if (!video) return;

    const faceMesh = new FaceMesh({
      locateFile: file =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5
    });

    faceMesh.onResults(async results => {
      if (!results.multiFaceLandmarks?.length) {
        setAllowCapture(false);

        clearCanvas();

        setFaceDetected({
          face: false,
          details: {
            confidence: 0,
            name: ''
          }
        });

        return;
      }

      const result = await recognizeFace(video);

      if (landmark) {
        drawLandmarks(results);
      }

      if (!result) return;

      const { detection, label } = result;

      const isMatched = label && label !== 'Unknown';

      setFaceDetected({
        face: true,
        details: {
          confidence: 0,
          name: label
        }
      });

      clearCanvas();

      // if (isMatched) {
      //   drawResult(video, detection, label);

      //   setAllowCapture(true);
      // } else {
      //   if (landmark) {
      //     drawLandmarks(results);
      //   }

      //   setAllowCapture(false);
      // }

      if (isMatched) {
        drawResult(video, detection, label);

        setAllowCapture(true);

        // Submit only once
        if (!submittedRef.current) {
          submittedRef.current = true;

          // Wait for allowCapture state to update
          setTimeout(async () => {
            try {
              await capture();

              // Keep dialog open for 1 second after successful capture
              setTimeout(() => {
                handleClose();
              }, 500);
            } catch (err) {
              console.error(err);
              submittedRef.current = false;
            }
          }, 1000);
        }
      } else {
        if (landmark) {
          drawLandmarks(results);
        }

        setAllowCapture(false);
      }
    });

    faceMeshRef.current = faceMesh;

    const camera = new Camera(video, {
      onFrame: async () => {
        await faceMesh.send({ image: video });
      },
      width: 640,
      height: 480
    });

    camera.start();

    cameraRef.current = camera;
  };

  async function uploadImages(imageSrc, datetime) {
    const requests = [];

    const selfieFormData = new FormData();
    const selfieFile = dataURLToFile(imageSrc, datetime + '_selfie.png');
    selfieFormData.append('file', selfieFile);

    requests.push({
      method: 'POST',
      url: `/trans/hris/geotag/images/${selectedRef.current[0]}`,
      data: selfieFormData
    });

    return await request.multiFetch(requests);
  }

  const capture = useCallback(
    async idSelected => {
      const imageSrc = webcamRef.current.getScreenshot();

      const targetKB = 500;
      const targetBytes = targetKB * 1024;

      let quality = 0.7;
      const maxSize = 225;

      let img;
      let currentBytes = Infinity;

      const maxAttempts = 10;

      for (let i = 0; i < maxAttempts; i++) {
        img = await compressImageToTargetSize(imageSrc, maxSize, quality);

        const base64 = img.split(',')[1];
        currentBytes = Math.ceil((base64.length * 3) / 4);

        console.log(
          `Attempt ${i + 1}:`,
          `quality=${quality.toFixed(2)}`,
          `size=${(currentBytes / 1024).toFixed(2)} KB`
        );

        if (Math.abs(currentBytes - targetBytes) <= targetBytes * 0.05) {
          break;
        }

        if (currentBytes > targetBytes) {
          quality -= 0.1;
        } else {
          quality += 0.05;
        }

        quality = Math.max(0.05, Math.min(1, quality));
      }

      console.log(`Final size: ${(currentBytes / 1024).toFixed(2)} KB`);

      console.log('Final quality:', quality);

      const dateTime =
        now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0');

      setImgSrc(imageSrc);

      // if (!allowCapture) return;

      if (!currentLocation) {
        notify.error('Location not found!');
        return;
      }

      const location = `${currentLocation.latitude}, ${currentLocation.longitude}`;

      // console.log('selectedwwww', selected);

      for (const item of tableData) {
        console.log('selectedRef.current[0]', selectedRef.current[0]);
        // console.log('selected[0]', selected);
        // console.log('item.ixSub', item.ixSub);
        // console.log('item', item);
        if (item.ixSub === selectedRef.current[0]) {
          // console.log('console');

          const logs = Object.keys(item)
            .filter(key => key.startsWith('log'))
            .sort((a, b) => {
              const aNum = Number(a.replace('log', ''));
              const bNum = Number(b.replace('log', ''));

              return aNum - bNum;
            });

          const lastLogKey = logs[logs.length - 1];

          if (lastLogKey) {
            const lastLogValue = item[lastLogKey];

            const lastDate = new Date(lastLogValue);

            const diff = (now.getTime() - lastDate.getTime()) / 1000 / 5;

            if (diff < 1) {
              return;
            }
          }

          const nextLogNumber = logs.length + 1;

          const nextLogKey = `log${nextLogNumber}`;

          item[nextLogKey] = now.toLocaleString();

          try {
            const res = await request.post('/trans/hris/geotag', {
              ixSub: item.ixSub,
              location,
              remarks: 'Face Time IN/OUT'
            });

            if (res?.success) {
              notify.success(res.data?.msg || 'Success');
            } else {
              notify.error(res.data?.msg || 'Something went wrong');
            }
          } catch (err) {
            console.error(err);
          }

          try {
            const uploadRes = await uploadImages(img, dateTime);
            if (uploadRes.some(res => !res.success)) {
              notify.error(
                uploadRes?.errorMessage || 'Failed to upload images.'
              );
            } else {
              notify.success('Successfully logged');
            }
          } catch (err) {
            console.error(err);
          }
          break;
        }
      }
    },
    [allowCapture, currentLocation, tableData, selected]
  );

  const init = async mounted => {
    await loadModels();

    if (!mounted) return;

    initFaceMesh();
  };

  const handleSelectAll = event => {
    if (event.target.checked) {
      const allIds = value.map(row => row.ixEmp);

      setSelected(allIds);
    } else {
      setSelected([]);
    }
  };

  const handleSelect = async ixEmp => {
    try {
      const isSelected = selected.includes(ixEmp);

      if (isSelected) {
        setSelected([]);

        setEmpData([]);
        setImgSrc([]);
        setBioID('');

        setAllowCapture(false);

        setFaceDetected({
          face: false,
          details: {
            confidence: 0,
            name: ''
          }
        });

        faceMatcherRef.current = null;

        clearCanvas();

        return;
      }

      setSelected([ixEmp]);
      selectedRef.current = [ixEmp];

      const res = await request.get(`/setup/sub/${ixEmp}`);
      const faceAuth = res?.data?.kvs?.find(item => item?.key === 'faceAuth');

      const data = faceAuth?.value || {};

      setEmpData(data);
      setImgSrc(data);

      await handleSetBioID({
        id: data?.id,
        images: data?.images || []
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    submittedRef.current = false;

    // Stop MediaPipe Camera
    if (cameraRef.current) {
      cameraRef.current.stop();
      cameraRef.current = null;
    }

    // Close FaceMesh
    if (faceMeshRef.current) {
      faceMeshRef.current.close();
      faceMeshRef.current = null;
    }

    // Stop browser camera tracks
    const video = webcamRef.current?.video;

    if (video?.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }

    faceMatcherRef.current = null;

    setAllowCapture(false);
    setSearch('');

    setSelected([]);
    setEmpData([]);
    setImgSrc([]);
    setBioID('');

    setFaceDetected({
      face: false,
      details: {
        confidence: 0,
        name: ''
      }
    });

    clearCanvas();

    setOpenBioSelfie(false);
  };

  const handleOpen = async () => {
    submittedRef.current = false;
    try {
      setOpenBioSelfie(true);

      setLoadingModels(true);
      setLoadingTable(true);

      navigator.geolocation.getCurrentPosition(
        position => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        () => {
          notify.error('Failed to update location.');
        }
      );

      await init(true);

      const payload = {
        ixSubType: settings?.ixSubType || 0,
        q: '',
        showInactive: false
      };
      const res = await request.post('/lib/sub', payload);

      // Add hasFaceImages flag to each employee
      const employeesWithFlag = await Promise.all(
        (res?.data?.sub_list || []).map(async emp => {
          try {
            const empRes = await request.get(`/setup/sub/${emp.ixSub}`);
            const faceAuth = empRes?.data?.kvs?.find(
              item => item?.key === 'faceAuth'
            );
            const images = faceAuth?.value?.images || [];
            return {
              ...emp,
              hasFaceImages: images && images.length > 0
            };
          } catch (err) {
            console.error(`Error checking face images for ${emp.ixSub}:`, err);
            return {
              ...emp,
              hasFaceImages: false
            };
          }
        })
      );

      setTableData(employeesWithFlag);
      setResEmpData(employeesWithFlag);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingModels(false);
      setLoadingTable(false);
    }
  };

  // useEffect(() => {
  //   let mounted = true;
  //   init(mounted);

  //   return () => {
  //     mounted = false;

  //     if (cameraRef.current) {
  //       cameraRef.current.stop();
  //     }
  //   };
  // }, []);

  return (
    <FaceBioContext.Provider
      value={{
        openBioSelfie,
        setOpenBioSelfie,

        handleOpen,
        handleClose,

        webcamRef,
        canvasRef,

        loadingModels,

        faceDetected,

        allowCapture,

        capturedImage,

        capture,

        bioID,

        handleSetBioID,

        initFaceMesh,

        selected,
        setSelected,

        handleSelectAll,
        handleSelect,

        tableData,
        setTableData,
        resEmpData,
        search,
        setSearch,
        loadingTable
      }}
    >
      {children}
    </FaceBioContext.Provider>
  );
}

export const useFaceBio = () => {
  const ctx = useContext(FaceBioContext);

  if (!ctx) {
    throw new Error('useFaceBio must be used within FaceBioProvider');
  }

  return ctx;
};

export default FaceBioProvider;
